﻿import type { Agent } from '@vexalyn/agent';
import type { AgentEvent } from '@vexalyn/agent';
import { computeUnifiedDiff, formatDiffText } from '@vexalyn/tools';
import { readFile } from 'node:fs/promises';
import { createLoadingAnimation, createSpinner, type Spinner } from './spinner.js';
import { globalTaskTracker } from './task-tracker.js';
import { formatToolError, formatToolLine, green as uiGreen, grey as uiGrey, reset as uiReset, bold as uiBold } from './ui/format.js';
import { takeSnapshots } from './ui/diff-cache.js';

const reset = '\u001b[0m';
const green = '\u001b[32m';
const silentUiTools = new Set(['create_plan', 'create_tasks', 'complete_task']);
const autoProgressTools = new Set(['create_directory', 'write_file', 'edit_file', 'apply_patch', 'delete_file', 'run_command']);

// Track the currently active loader so approve callbacks can stop it before printing
let _activeLoader: { stop(): void } | null = null;

/** Stop the loading animation immediately - call this before writing permission prompts. */
export function stopActiveLoader(): void {
  if (_activeLoader) {
    _activeLoader.stop();
    _activeLoader = null;
  }
}

// Display mutex: ensures permission prompts never appear mid-typing-effect
let _displayDone: Promise<void> = Promise.resolve();
let _displayResolve: (() => void) | null = null;
let _displayDepth = 0;

export function waitForDisplay(): Promise<void> {
  return _displayDone;
}

function acquireDisplay(): void {
  if (_displayDepth === 0) {
    _displayDone = new Promise<void>(r => { _displayResolve = r; });
  }
  _displayDepth++;
}

function releaseDisplay(): void {
  if (--_displayDepth === 0 && _displayResolve) {
    const resolve = _displayResolve;
    _displayResolve = null;
    _displayDone = Promise.resolve();
    resolve();
  }
}

/** Acquire the display mutex and return a function to release it.
 *  Use this to hold the mutex across async user-input waits (e.g. permission prompts).
 */
export function holdDisplay(): () => void {
  acquireDisplay();
  return releaseDisplay;
}

function markTerminalOutput(): void {
  globalTaskTracker.markOutputInterrupted();
}

function isRawToolJson(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return false;
  try {
    const parsed = JSON.parse(trimmed) as { tool?: unknown; arguments?: unknown; input?: unknown };
    return typeof parsed.tool === 'string' && ('arguments' in parsed || 'input' in parsed);
  } catch {
    return false;
  }
}

function removeRawToolJson(text: string): { text: string; found: boolean } {
  let output = text;
  let found = false;
  const patterns = [
    /\{\s*"tool"\s*:\s*"[^"]+"\s*,\s*"(?:input|arguments)"\s*:\s*\{[\s\S]*?\}\s*\}/g,
    /\{\s*'tool'\s*:\s*'[^']+'\s*,\s*'(?:input|arguments)'\s*:\s*\{[\s\S]*?\}\s*\}/g,
  ];

  for (const pattern of patterns) {
    pattern.lastIndex = 0;
    if (pattern.test(output)) {
      found = true;
      pattern.lastIndex = 0;
      output = output.replace(pattern, '');
    }
  }

  return { text: output, found };
}

function removeToolMarkup(text: string): { text: string; found: boolean } {
  let output = text;
  let found = false;
const patterns = [
    /<\|DSML\|tool_calls>[\s\S]*?<\/\|DSML\|tool_calls>/g,
    /<｜DSML｜tool_calls>[\s\S]*?<\/｜DSML｜tool_calls>/g,
    /<tool_calls>[\s\S]*?<\/tool_calls>/g,
  ];

  for (const pattern of patterns) {
    pattern.lastIndex = 0;
    if (pattern.test(output)) {
      found = true;
      pattern.lastIndex = 0;
      output = output.replace(pattern, '');
    }
  }

  return { text: output, found };
}

function createMarkdownRenderer() {
  let inCodeBlock = false;
  let codeBlockLang = '';
  let codeLineNumber = 1;

  return (line: string): { text: string; isCode: boolean } => {
    if (line.trim().startsWith('```')) {
      const wasInCodeBlock = inCodeBlock;
      inCodeBlock = !inCodeBlock;
      if (inCodeBlock) {
        codeBlockLang = line.trim().substring(3).trim();
        codeLineNumber = 1;
        return {
          text: `${uiGrey}\u250c Code${codeBlockLang ? ` \u2014 ${codeBlockLang}` : ''}${uiReset}`,
          isCode: true,
        };
      } else {
        inCodeBlock = false;
        return { text: `${uiGrey}\u2514${uiReset}`, isCode: wasInCodeBlock };
      }
    }

    if (inCodeBlock) {
      const numberLabel = `${String(codeLineNumber).padStart(2, ' ')}. `;
      codeLineNumber++;
      return { text: `${uiGrey}  ${numberLabel}${line}${uiReset}`, isCode: true };
    }

    let formatted = line;

    if (formatted.startsWith('# ')) {
      return { text: `\n${uiBold}${formatted.substring(2)}${uiReset}`, isCode: false };
    } else if (formatted.startsWith('## ')) {
      return { text: `\n${uiBold}${formatted.substring(3)}${uiReset}`, isCode: false };
    } else if (formatted.startsWith('### ')) {
      return { text: `\n${uiBold}${formatted.substring(4)}${uiReset}`, isCode: false };
    }

    if (formatted.trim().startsWith('* ') || formatted.trim().startsWith('- ')) {
      const indent = formatted.substring(0, formatted.indexOf(formatted.trim()));
      const content = formatted.trim().substring(2);
      formatted = `${indent}\u001b[32m\u2022\u001b[0m ${content}`;
    } else {
      const numListMatch = formatted.match(/^(\s*)(\d+)\.\s(.*)/);
      if (numListMatch) {
        const [, indent, num, content] = numListMatch;
        formatted = `${indent}\u001b[32m${num}.\u001b[0m ${content}`;
      }
    }

    formatted = formatted.replace(/`([^`]+)`/g, '\u001b[36m$1\u001b[0m');
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '\u001b[1m$1\u001b[22m');
    formatted = formatted.replace(/__([^_]+)__/g, '\u001b[1m$1\u001b[22m');
    formatted = formatted.replace(/\*([^*]+)\*/g, '\u001b[3m$1\u001b[23m');
    formatted = formatted.replace(/_([^_]+)_/g, '\u001b[3m$1\u001b[23m');

    return { text: formatted, isCode: false };
  };
}

export async function writeAgentResponse(
  agent: Agent,
  message: string,
  write: (chunk: string) => void = (chunk) => process.stdout.write(chunk),
): Promise<void> {
  const renderLine = createMarkdownRenderer();
  let lineBuffer = '';
  for await (const chunk of agent.stream(message)) {
    lineBuffer += chunk;
    let newlineIndex;
    while ((newlineIndex = lineBuffer.indexOf('\n')) !== -1) {
      const line = lineBuffer.substring(0, newlineIndex);
      lineBuffer = lineBuffer.substring(newlineIndex + 1);
      const rendered = renderLine(line);
      write(rendered.text + '\n');
    }
  }
  if (lineBuffer.length > 0) {
    const rendered = renderLine(lineBuffer);
    write(rendered.text);
  }
  write('\n');
}

export async function writeAgentEventResponse(
  agent: Agent,
  message: string,
  write: (chunk: string) => void = (chunk) => process.stdout.write(chunk),
): Promise<void> {
  const pendingSearchMatches = new Set<string>();
  let hasSearchResults = false;
  let hasPrintedAgentPrefix = false;

  const flushSearchSummary = async (): Promise<void> => {
    if (!hasSearchResults) return;
    write(`${green}\u2713 ${pendingSearchMatches.size} matches found${reset}\n`);
    pendingSearchMatches.clear();
    hasSearchResults = false;
  };

  const strInput = (input: unknown, key: string): string => {
    if (!input || typeof input !== 'object') return '';
    const value = (input as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : '';
  };

  const extractStderrTail = (value: unknown): string => {
    if (!value || typeof value !== 'object') return '';
    const record = value as Record<string, unknown>;
    const tail = typeof record['stderr'] === 'string' && record['stderr'].trim().length > 0
      ? record['stderr']
      : typeof record['stdout'] === 'string'
        ? record['stdout']
        : '';
    const lines = tail.trim().split('\n').filter((line) => line.trim().length > 0).slice(-3);
    if (lines.length === 0) return '';
    return lines.map((line) => `  | ${line}`).join('\n');
  };

  /** Draw colored before/after diffs for every file mutated this turn. */
  const renderMutationDiffs = async (): Promise<void> => {
    const snapshots = takeSnapshots();
    for (const snapshot of snapshots) {
      let current = '';
      try {
        current = await readFile(snapshot.absPath, 'utf8');
      } catch {
        current = '';
      }
      const hunks = computeUnifiedDiff(snapshot.originalContent ?? '', current);
      markTerminalOutput();
      if (hunks.length === 0) {
        write(`${uiGreen}  \u2713 ${snapshot.displayPath} (tanpa perubahan isi)${uiReset}\n`);
        continue;
      }
      write(`${uiGrey}  \u250c ${snapshot.displayPath}${uiReset}\n`);
      const rendered = formatDiffText(hunks, { maxLines: 30 });
      write(`${rendered.split('\n').map((row) => `  ${row}`).join('\n')}\n`);
    }
  };

  const renderLine = createMarkdownRenderer();
  let lineBuffer = '';
  let lastToolInput: unknown = null;
  let lastToolName: string | null = null;
  let suppressAgentText = false;
  let toolThinking: Spinner | null = null;

  // -- Loading animation (shown while waiting for the first AI event) --
  // Only used in interactive terminal mode - skipped in tests to keep output clean.
  const isTest = process.env.VITEST === 'true';
  const loader = isTest ? null : createLoadingAnimation('Menghubungi model AI...');
  if (loader) loader.start();
  let spinnerStopped = false;

  const PURPLE = '\x1b[38;2;167;139;250m';
  const BOLD   = '\x1b[1m';
  const RST    = '\x1b[0m';
  const agentPrefix = `\n${PURPLE}\u25C6${RST} ${BOLD}Vexalyn Agent${RST} ${PURPLE}\u203A${RST} `;

  // Register this loader so approve callback can stop it before writing permission prompt
  _activeLoader = loader;

  // Pre-tool text buffer: text-delta that arrives before the first tool-call is buffered
  // and flushed to stdout BEFORE the permission prompt appears (so AI can "announce" the action).
  let preToolTextBuffer = '';
  let preToolTextFlushed = false;
  let firstToolCallSeen = false;
  let toolMarkupBuffer = '';
  let suppressRawToolMarkup = false;

  /** Flush pre-tool announcement text (if any) before showing permission prompt or tool output */
  const flushPreToolText = async (): Promise<void> => {
    if (preToolTextFlushed || preToolTextBuffer.trim().length === 0) return;
    if (isRawToolJson(preToolTextBuffer)) {
      preToolTextBuffer = '';
      return;
    }
    preToolTextFlushed = true;
    // Write synchronously (no typing delay) so it's fully on screen before approve() fires
    const renderPre = createMarkdownRenderer();
    markTerminalOutput();
    write(agentPrefix);
    hasPrintedAgentPrefix = true;
    const lines = preToolTextBuffer.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const rendered = renderPre(lines[i]!);
      write(rendered.text + (i < lines.length - 1 ? '\n' : ''));
    }
    write('\n');
    preToolTextBuffer = '';
  };

  const stopSpinner = () => {
    if (!spinnerStopped) {
      if (loader) loader.stop();
      _activeLoader = null;
      spinnerStopped = true;
    }
  };

  const startToolThinking = (toolName?: string | null) => {
    if (isTest || toolThinking || silentUiTools.has(toolName ?? '')) return;
    toolThinking = createSpinner('Thinking').start();
    _activeLoader = toolThinking;
  };

  const stopToolThinking = () => {
    if (!toolThinking) return;
    toolThinking.stop();
    toolThinking = null;
    if (_activeLoader) _activeLoader = null;
    process.stdout.write('\r\x1b[K');
  };

  try {
    for await (const event of agent.streamEvents(message)) {
      if (event.type === 'text-delta') {
        loader?.updateText('Merangkai jawaban...');
      }
      stopSpinner();

      if (event.type === 'text-delta') {
        stopToolThinking();
        if (suppressAgentText) continue;
        let textDelta = event.text ?? '';
        const combinedToolMarkup = removeToolMarkup(toolMarkupBuffer + textDelta);
        const combinedRawToolJson = removeRawToolJson(toolMarkupBuffer + textDelta);
        if (combinedToolMarkup.found) {
          preToolTextBuffer = '';
          lineBuffer = '';
          textDelta = '';
          toolMarkupBuffer = '';
          suppressRawToolMarkup = false;
          startToolThinking('raw_tool_markup');
        } else if (combinedRawToolJson.found) {
          preToolTextBuffer = '';
          lineBuffer = '';
          textDelta = combinedRawToolJson.text;
          toolMarkupBuffer = '';
          suppressRawToolMarkup = false;
          if (!textDelta.trim()) startToolThinking('raw_tool_json');
} else if (suppressRawToolMarkup || textDelta.includes('<|DSML|tool_calls>') || textDelta.includes('<｜DSML｜tool_calls>') || textDelta.includes('<tool_calls>')) {
          suppressRawToolMarkup = true;
          toolMarkupBuffer += textDelta;
          if (toolMarkupBuffer.includes('</|DSML|tool_calls>') || toolMarkupBuffer.includes('</｜DSML｜tool_calls>') || toolMarkupBuffer.includes('</tool_calls>')) {
            removeToolMarkup(toolMarkupBuffer);
            textDelta = '';
            toolMarkupBuffer = '';
            suppressRawToolMarkup = false;
            preToolTextBuffer = '';
            lineBuffer = '';
            startToolThinking('raw_tool_markup');
          } else {
            startToolThinking('raw_tool_markup');
            continue;
          }
        } else if (textDelta.includes('<|DSML|') || textDelta.includes('</|DSML|') || textDelta.includes('<｜DSML｜') || textDelta.includes('</｜DSML｜')) {
          startToolThinking('raw_tool_markup');
          continue;
        }

        // If no tool has fired yet, buffer silently instead of printing immediately.
        // This prevents pre-tool "announcement" text from appearing before the permission prompt.
        if (!firstToolCallSeen) {
          preToolTextBuffer += textDelta;
          continue;
        }

        await flushSearchSummary();
        if (!hasPrintedAgentPrefix) {
          markTerminalOutput();
          if (firstToolCallSeen) write(agentPrefix);
          else await writeTypingEffect(agentPrefix, write);
          hasPrintedAgentPrefix = true;
        }
        lineBuffer += textDelta;
        let newlineIndex;
        while ((newlineIndex = lineBuffer.indexOf('\n')) !== -1) {
          const line = lineBuffer.substring(0, newlineIndex);
          lineBuffer = lineBuffer.substring(newlineIndex + 1);
          if (isRawToolJson(line)) continue;
          const rendered = renderLine(line);
          await writeTypingEffect(rendered.text + '\n', write);
        }
      }

      if (event.type === 'tool-call') {
        // Flush any pre-tool announcement text BEFORE the permission prompt fires
        await flushPreToolText();
        startToolThinking(event.name);

        // Discard any remaining pre-tool buffer
        preToolTextBuffer = '';
        firstToolCallSeen = true;

        if (lineBuffer.length > 0) {
          if (isRawToolJson(lineBuffer)) {
            lineBuffer = '';
            continue;
          }
          const rendered = renderLine(lineBuffer);
          write(rendered.text + '\n');
          lineBuffer = '';
        }
        hasPrintedAgentPrefix = false;
        lastToolInput = event.input;
        lastToolName = event.name ?? null;
        const toolName = lastToolName ?? '';
        // Instant one-line tool indicator - printed the moment the call starts,
        // long before execution finishes. Tools run silently behind this line.
        if (!silentUiTools.has(toolName)) {
          markTerminalOutput();
          write(formatToolLine(toolName, lastToolInput as Record<string, unknown> | undefined | null) + '\n');
        }
        // Suppress verbose agent response when the LAST tool is a write/mutate op or returns detailed results.
        const writeTools = ['write_file', 'edit_file', 'apply_patch', 'delete_file', 'run_command', 'search_files', 'web_search'];
        suppressAgentText = writeTools.includes(toolName);
      }

      if (event.type === 'tool-result') {
        stopToolThinking();
        const resultObject = event.result && typeof event.result === 'object'
          ? event.result as { ok?: unknown; error?: unknown; value?: unknown }
          : {};
        const failed = resultObject.ok === false;

        if (failed) {
          markTerminalOutput();
          const command = lastToolName === 'run_command' ? strInput(lastToolInput, 'command') : '';
          const exitCode = typeof resultObject.value === 'object' && resultObject.value !== null && 'exitCode' in (resultObject.value as object)
            ? (resultObject.value as { exitCode?: unknown }).exitCode
            : null;
          const fallback = command
            ? `${command} gagal${exitCode !== null ? ` (exit ${String(exitCode)})` : ''}`
            : `${lastToolName} gagal`;
          const message = typeof resultObject.error === 'string' && resultObject.error.trim().length > 0
            ? resultObject.error
            : fallback;
          write(formatToolError(message) + '\n');
          const errTail = lastToolName === 'run_command' ? extractStderrTail(resultObject.value) : '';
          if (errTail) write(`${uiGrey}${errTail}${uiReset}\n`);
          continue;
        }

        if (event.name === 'search_files' && isToolResultWithArray(event.result)) {
          for (const value of event.result.value) {
            if (typeof value === 'string') pendingSearchMatches.add(value);
          }
          hasSearchResults = true;
        }

        // Render detailed results for specific tools
        if (event.name === 'web_search' && event.result && typeof event.result === 'object' && 'ok' in event.result) {
          const result = event.result as { ok: boolean; value?: unknown };
          if (result.ok && Array.isArray(result.value)) {
            for (const item of result.value) {
              if (typeof item === 'object' && item !== null) {
                const { title, snippet } = item as { title?: string; snippet?: string };
                if (title) write(`${uiBold}${title}${uiReset}\n`);
                if (snippet) write(`${uiGrey}${snippet}${uiReset}\n`);
              }
            }
          }
        }

        if (event.name === 'write_file' && lastToolInput && typeof lastToolInput === 'object') {
          const { content } = lastToolInput as { content?: string };
          if (typeof content === 'string') {
            const lines = content.split('\n').slice(0, 5);
            for (let i = 0; i < lines.length; i++) {
              write(`${uiGrey}${String(i + 1).padStart(2, ' ')}. ${lines[i]}${uiReset}\n`);
            }
          }
        }

        if (event.name === 'run_command' && event.result && typeof event.result === 'object' && 'ok' in event.result) {
          const result = event.result as { ok: boolean; value?: unknown };
          if (result.ok && typeof result.value === 'object' && result.value !== null) {
            const { stdout } = result.value as { stdout?: string };
            if (typeof stdout === 'string' && stdout.trim()) {
              const sanitized = stdout.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
              const lines = sanitized.split('\n').slice(0, 10);
              for (const line of lines) {
                write(`${uiGrey}${line}${uiReset}\n`);
              }
            }
          }
        }

        await renderMutationDiffs();
        if (autoProgressTools.has(event.name ?? '') && !isTest) {
          globalTaskTracker.completeNextPending();
        }
      }
    }
} finally {
    stopToolThinking();
    stopSpinner();
  }
  
  await flushSearchSummary();
  if (!suppressAgentText) {
    // If no tool was ever called, flush the pre-tool buffer as normal response text
    if (!firstToolCallSeen && preToolTextBuffer.length > 0) {
      markTerminalOutput();
      await writeTypingEffect(agentPrefix, write);
      const renderLineFinal = createMarkdownRenderer();
      const lines = preToolTextBuffer.split('\n');
      for (let i = 0; i < lines.length; i++) {
        const rendered = renderLineFinal(lines[i]!);
        write(rendered.text + (i < lines.length - 1 ? '\n' : ''));
      }
    }
    if (lineBuffer.length > 0) {
      if (isRawToolJson(lineBuffer)) {
        lineBuffer = '';
        await flushSearchSummary();
        write('\n');
        return;
      }
      const rendered = renderLine(lineBuffer);
      if (firstToolCallSeen) {
        markTerminalOutput();
        await writeTypingEffect(rendered.text, write);
      } else {
        markTerminalOutput();
        write(rendered.text);
}
    }
  }
  write('\n');
}

async function writeTypingEffect(
  text: string,
  write: (chunk: string) => void,
): Promise<void> {
  write(text);
}

function isToolResultWithArray(result: unknown): result is { value: unknown[] } {
  return typeof result === 'object' && result !== null && 'value' in result && Array.isArray(result.value);
}

export type { AgentEvent };
